import {
    doc,
    collection,
    onSnapshot,
    query,
    orderBy,
    setDoc,
    updateDoc,
    addDoc,
    serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase.utils';

/* ─── Queue (main Whatnot page) ───────────────────────────────────────────── */

/**
 * Subscribes to show cards ordered by explicit `order` field (set on add,
 * updated on drag-and-drop reorder). Cards without an `order` field appear first.
 */
export function listenToShowCards(uid, showId, callback) {
    const colRef = collection(db, 'users', uid, 'shows', showId, 'cards');
    const q = query(colRef, orderBy('order', 'asc'));
    return onSnapshot(
        q,
        (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        () => callback([])
    );
}

/* ─── Show document ───────────────────────────────────────────────────────── */

/**
 * Subscribes to a show document (name, currentCardIndex, status).
 */
export function listenToShowState(uid, showId, callback) {
    const ref = doc(db, 'users', uid, 'shows', showId);
    return onSnapshot(
        ref,
        (snap) => callback(snap.exists() ? snap.data() : { currentCardIndex: 0 }),
        () => callback({ currentCardIndex: 0 })
    );
}

/**
 * Merges data into a show document (currentCardIndex, name, status, etc.).
 */
export async function updateShowState(uid, showId, data) {
    const ref = doc(db, 'users', uid, 'shows', showId);
    await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
}

/* ─── Show cards subcollection ────────────────────────────────────────────── */

/**
 * Copies a card from the queue into a specific show's cards subcollection.
 */
export async function addCardToShow(uid, showId, cardData) {
    const colRef = collection(db, 'users', uid, 'shows', showId, 'cards');
    const docRef = await addDoc(colRef, {
        // Card identity
        cardNumber:      cardData.cardNumber ?? null,
        name:            cardData.name ?? [],
        team:            cardData.team ?? [],
        cardAttributes:  cardData.cardAttributes ?? [],
        // Variation
        cardParallelName: cardData.cardParallelName ?? null,
        cardColor:        cardData.cardColor ?? null,
        cardPrintNumber:  cardData.cardPrintNumber ?? null,
        cardSupply:       cardData.cardSupply ?? null,
        // Checklist context
        sport:            cardData.sport ?? null,
        year:             cardData.year ?? null,
        manufacturer:     cardData.manufacturer ?? null,
        series:           cardData.series ?? null,
        cardType:         cardData.cardType ?? null,
        cardTypeCategory: cardData.cardTypeCategory ?? null,
        // The staged transaction this copy came from. Mark Listed promotes that row.
        sourceTransactionId: cardData.id ?? cardData.sourceTransactionId ?? null,
        listedSaleId: null,
        // Show metadata
        sold:     false,
        finalBid: null,
        notes:    '',
        order:    Date.now(), // large int — ensures append ordering; normalized to 0,1,2… after first reorder
        addedAt:  serverTimestamp(),
    });
    return docRef.id;
}

/**
 * Removes a card from a show's cards subcollection.
 */
export async function removeCardFromShow(uid, showId, cardId) {
    const { deleteDoc } = await import('firebase/firestore');
    const ref = doc(db, 'users', uid, 'shows', showId, 'cards', cardId);
    await deleteDoc(ref);
}

/**
 * Updates show-specific metadata on a show card (sold, finalBid, notes).
 */
export async function updateShowCard(uid, showId, cardId, data) {
    const ref = doc(db, 'users', uid, 'shows', showId, 'cards', cardId);
    await updateDoc(ref, data);
}

/* ─── Shows list ──────────────────────────────────────────────────────────── */

/**
 * Subscribes to the user's shows collection, newest first.
 */
export function listenToWhatnotShows(uid, callback) {
    const colRef = collection(db, 'users', uid, 'shows');
    const q = query(colRef, orderBy('createdAt', 'desc'));
    return onSnapshot(
        q,
        (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        () => callback([])
    );
}

/**
 * Deletes a show document and all cards in its subcollection.
 */
export async function deleteShow(uid, showId) {
    const { deleteDoc, getDocs } = await import('firebase/firestore');
    const cardsRef = collection(db, 'users', uid, 'shows', showId, 'cards');
    const snap = await getDocs(cardsRef);
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    await deleteDoc(doc(db, 'users', uid, 'shows', showId));
}

/**
 * Creates a new show document.
 * @returns {Promise<string>} The new show's document ID
 */
export async function createWhatnotShow(uid, title) {
    const colRef = collection(db, 'users', uid, 'shows');
    const docRef = await addDoc(colRef, {
        name:              title,
        status:            'draft',
        currentCardIndex:  0,
        createdAt:         serverTimestamp(),
    });
    return docRef.id;
}
